import React from 'react';
import type { ConnectionPhase } from '../services/connectionJourneyService';

type NextStepActionsProps = {
  disabled?: boolean;
  connectionName?: string;
  nextPhaseName?: string;
  nextPhaseCandidate?: ConnectionPhase | null;
  onDirectCall: (phase?: 'call' | 'video' | 'physical') => void;
  onAbortFlow: () => void;
  onPlanAgenda: () => void;
  onEndWithoutPhysical: (reason: string) => void;
  onClose: () => void;
};

// Fase configuratie - centrale bron van waarheid
const PHASE_ACTION_CONFIG = {
  call: {
    icon: '📞',
    action: 'Direct bellen met',
    description: 'Start nu meteen de belfase'
  },
  video: {
    icon: '📹',
    action: 'Direct video bellen met',
    description: 'Start nu meteen de beeldbelfase'
  },
  physical: {
    icon: '🏢',
    action: 'Direct afspraak starten met',
    description: 'Start nu meteen de fysieke afspraak fase'
  }
} as const;

const NextStepActions: React.FC<NextStepActionsProps> = ({
  disabled,
  connectionName = 'deze persoon',
  nextPhaseName = 'Bellen',
  nextPhaseCandidate,
  onDirectCall,
  onAbortFlow,
  onPlanAgenda,
  onEndWithoutPhysical,
  onClose
}) => {
  const [endReason, setEndReason] = React.useState('');

  const MIN_REASON = 10;
  const MAX_REASON = 280;

  const isPhysicalFlow =
    nextPhaseCandidate === 'physical' ||
    (nextPhaseName || '').toLowerCase().includes('fysiek');

  const trimmedReason = endReason.trim();
  const reasonTooShort = trimmedReason.length > 0 && trimmedReason.length < MIN_REASON;
  const reasonTooLong = trimmedReason.length > MAX_REASON;
  const canSubmitEnd =
    trimmedReason.length >= MIN_REASON && trimmedReason.length <= MAX_REASON;
  // Bepaal actie configuratie op basis van nextPhaseCandidate (primair) of nextPhaseName (fallback)
  const getPhaseAction = () => {
    // Primair: gebruik nextPhaseCandidate als die beschikbaar is.
    // 'completed' is a terminal state — it has no action config.
    const activePhases = ['call', 'video', 'physical'] as const;
    if (nextPhaseCandidate && (activePhases as readonly string[]).includes(nextPhaseCandidate)) {
      const config = PHASE_ACTION_CONFIG[nextPhaseCandidate as typeof activePhases[number]];
      if (config) return config;
    }

    // Fallback: parse nextPhaseName string (voor backwards compatibility)
    const lowerPhase = (nextPhaseName ?? '').toLowerCase();
    
    if (lowerPhase.includes('video') || lowerPhase === 'beeldbellen' || lowerPhase === 'videogesprek') {
      return PHASE_ACTION_CONFIG.video;
    }
    
    if (lowerPhase.includes('fysiek') || lowerPhase.includes('fysieke')) {
      return PHASE_ACTION_CONFIG.physical;
    }
    
    // Default: call/bellen
    return PHASE_ACTION_CONFIG.call;
  };

  const phaseAction = getPhaseAction();

  // Dedicated physical flow layout (geen directe start; eindigen met reden)
  if (isPhysicalFlow) {
    return (
      <div className="flex flex-col space-y-3">
        {/* Optie 1: Plannen via agenda */}
        <button
          onClick={onPlanAgenda}
          disabled={disabled}
          className="w-full px-4 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50 disabled:bg-gray-300 text-left transition-colors"
        >
          <div className="flex flex-col">
            <span className="font-semibold">📅 Plan fysieke afspraak</span>
            <span className="text-xs text-blue-100/90 mt-1">Kies datum/tijd en locatie (Maps)</span>
          </div>
        </button>

        {/* Optie 2: Later oppakken */}
        <button
          onClick={onAbortFlow}
          className="w-full px-4 py-3 rounded-lg bg-gray-100 border border-gray-300 text-gray-700 font-medium hover:bg-gray-200 text-left transition-colors"
        >
          <div className="flex flex-col">
            <span className="font-semibold">⏸️ Later oppakken</span>
            <span className="text-xs text-gray-600 mt-1">We sturen een herinnering in de inbox</span>
          </div>
        </button>

        {/* Optie 3: Beëindigen zonder fysieke afspraak (met reden) */}
        <div className="w-full rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="flex flex-col space-y-2">
            <div>
              <div className="font-semibold text-red-700">⛔ Beëindigen zonder fysieke afspraak</div>
              <div className="text-xs text-red-700/90">Reden is zichtbaar voor de ander</div>
            </div>
            <label className="text-xs font-medium text-gray-700" htmlFor="endReason">
              Waarom beëindigen? (min 10, max 280)
            </label>
            <textarea
              id="endReason"
              value={endReason}
              maxLength={MAX_REASON}
              onChange={(e) => setEndReason(e.target.value)}
              placeholder="Geef kort de reden zodat de ander het begrijpt"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-400 focus:ring-1 focus:ring-red-200"
              rows={3}
            />
            <div className="flex items-center justify-between text-xs">
              <span className={`text-${reasonTooShort || reasonTooLong ? 'red' : 'gray'}-600`}>
                {reasonTooShort
                  ? 'Vul minimaal 10 tekens in.'
                  : reasonTooLong
                  ? 'Maximaal 280 tekens.'
                  : 'Reden is verplicht.'}
              </span>
              <span className="text-gray-500">{`${trimmedReason.length}/${MAX_REASON}`}</span>
            </div>
            <button
              onClick={() => {
                if (!canSubmitEnd) return;
                onEndWithoutPhysical(trimmedReason);
              }}
              disabled={!canSubmitEnd || disabled}
              className="w-full px-4 py-3 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 disabled:bg-gray-300 text-left transition-colors"
            >
              Beëindigen en reden delen
            </button>
          </div>
        </div>

        {/* Sluiten knop (onderaan, minder prominent) */}
        <button
          onClick={onClose}
          className="w-full text-sm text-gray-500 hover:text-gray-700 underline pt-2"
        >
          Annuleren
        </button>
      </div>
    );
  }

  // Default (niet-fysiek): bestaande drie knoppen + annuleren
  return (
    <div className="flex flex-col space-y-3">
      {/* Optie 1: Direct starten */}
      <button
        onClick={() => {
          // 'completed' and 'chat' are not valid direct-call targets
          const callablePhases = ['call', 'video', 'physical'] as const;
          const phase =
            nextPhaseCandidate &&
            (callablePhases as readonly string[]).includes(nextPhaseCandidate)
              ? (nextPhaseCandidate as typeof callablePhases[number])
              : undefined;
          onDirectCall(phase);
        }}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600 disabled:opacity-50 disabled:bg-gray-300 text-left transition-colors"
      >
        <div className="flex flex-col">
          <span className="font-semibold">{phaseAction.icon} {phaseAction.action} {connectionName}</span>
          <span className="text-xs text-green-100/90 mt-1">{phaseAction.description}</span>
        </div>
      </button>

      {/* Optie 2: Plannen via agenda */}
      <button
        onClick={onPlanAgenda}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50 disabled:bg-gray-300 text-left transition-colors"
      >
        <div className="flex flex-col">
          <span className="font-semibold">📅 Plannen via agenda</span>
          <span className="text-xs text-blue-100/90 mt-1">Kies een moment via de agenda (met beschikbaarheid)</span>
        </div>
      </button>

      {/* Optie 3: Afbreken */}
      <button
        onClick={onAbortFlow}
        className="w-full px-4 py-3 rounded-lg bg-gray-100 border border-gray-300 text-gray-700 font-medium hover:bg-gray-200 text-left transition-colors"
      >
        <div className="flex flex-col">
          <span className="font-semibold">⏸️ Afbreken en later oppakken</span>
          <span className="text-xs text-gray-600 mt-1">Beide partijen krijgen een herinnering in de inbox</span>
        </div>
      </button>

      {/* Sluiten knop (onderaan, minder prominent) */}
      <button
        onClick={onClose}
        className="w-full text-sm text-gray-500 hover:text-gray-700 underline pt-2"
      >
        Annuleren
      </button>
    </div>
  );
};

export default NextStepActions;
